// Miembros (ChannelMembersSheet.swift): admins first, then the alphabet. Server-first both ways —
// the roster is refetched after every accepted change — and the verbs are offered only when this
// reader's own roles carry them (ChannelMembersPresentation.canManageMembers): a hidden button
// beats one that answers 403. The reader's own row never offers "remove": leaving is a different
// verb, and it lives on Información. A group message has no roster to edit; it offers what it
// CAN do — open the conversation for a larger set of people.
import { useCallback, useEffect, useMemo, useState, type KeyboardEvent, type MouseEvent } from 'react';
import { data } from '../../../bridge/client';
import { ChannelMembersPresentation, type Row } from '../../../core/channelMembersPresentation';
import type { ChannelRecord } from '../../../core/records';
import type { ChannelPanelSnapshot, Roster } from '../../../data/features/channelPanel';
import { t, tf } from '../../../i18n/i18n';
import { Avatar } from '../../Avatar';
import { Button, ConfirmButton, EmptyState, Group, InlineStatus, SearchInput } from '../../kit/Kit';
import { useShell } from '../../shell/ShellContext';
import { AddPeopleDialog } from './AddPeopleDialog';
import { usersById } from './panelModel';

export function MembersTab({ snap, channel }: { snap: ChannelPanelSnapshot; channel: ChannelRecord }) {
  const shell = useShell();
  const me = snap.currentUserId ?? '';
  const [roster, setRoster] = useState<Roster | null>(null);
  const [status, setStatus] = useState<'loading' | 'idle' | 'failed'>('loading');
  const [filter, setFilter] = useState('');
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => {
    setStatus('loading');
    return data.call('channelRoster', channel.id).then(
      (fresh) => {
        if (fresh) setRoster(fresh);
        setStatus(fresh ? 'idle' : 'failed');
      },
      () => setStatus('failed'),
    );
  }, [channel.id]);

  useEffect(() => {
    let alive = true;
    // What this device last knew paints first; the server's roster replaces it.
    void data.call('channelRosterCached', channel.id).then((cached) => alive && setRoster((r) => r ?? (cached.members.length ? cached : null)));
    void load();
    return () => {
      alive = false;
    };
  }, [channel.id, load]);

  const rows = useMemo(
    () =>
      roster
        ? ChannelMembersPresentation.rows({ members: roster.members, usersById: usersById(roster.users), currentUserId: me, unknownLabel: t('channel.reaction.unknown') })
        : [],
    [roster, me],
  );
  const ownRoles = roster?.fresh ? (roster.members.find((m) => m.userId === me)?.roles ?? '') : '';
  const canManage = !!roster?.fresh && channel.deleteAt === 0 && ChannelMembersPresentation.canManageMembers(channel.type, ownRoles);
  const group = ChannelMembersPresentation.groupOptions(channel.type, rows.length);

  const needle = filter.trim().toLowerCase().replace(/^@/, '');
  const visible = needle ? rows.filter((r) => r.displayName.toLowerCase().includes(needle) || r.username.toLowerCase().includes(needle)) : rows;

  const remove = (row: Row) => {
    data.call('channelRemoveMember', channel.id, row.userId).then(
      (ok) => (ok ? void load() : shell.toast(t('people.removeFailed'), 'error')),
      () => shell.toast(t('people.removeFailed'), 'error'),
    );
  };

  const open = (row: Row, e: MouseEvent | KeyboardEvent) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    shell.openUser({ userId: row.userId }, { x: rect.left, y: rect.top + rect.height / 2 });
  };

  return (
    <>
      <div className="cp-toolbar">
        <SearchInput value={filter} onChange={setFilter} placeholder={t('web.channelPanel.membersFilter')} />
        {canManage ? (
          <Button kind="primary" icon="plus" onClick={() => setAdding(true)}>
            {t('people.add')}
          </Button>
        ) : null}
      </div>
      {status === 'failed' ? <InlineStatus kind="error" text={t('web.channelPanel.rosterFailed')} onRetry={() => void load()} /> : null}
      {!roster && status === 'loading' ? <InlineStatus kind="loading" text={t('channel.loading.accessibility')} /> : null}

      {roster && !visible.length && needle ? <EmptyState title={tf('web.channelPanel.membersNoMatch', filter.trim())} /> : null}
      {visible.length ? (
        <Group title={snap.memberCount !== null && !needle ? tf('channel.info.members', snap.memberCount) : undefined}>
          {visible.map((row) => (
            <div
              key={row.userId}
              className="list-row clickable cp-member"
              role="button"
              tabIndex={0}
              onClick={(e) => open(row, e)}
              onKeyDown={(e) => e.key === 'Enter' && open(row, e)}
            >
              <span className="list-row-leading">
                <Avatar userId={row.userId} name={row.displayName} size={32} version={row.lastPictureUpdate} />
              </span>
              <span className="list-row-text">
                <span className="list-row-title">{row.isSelf ? tf('people.yourself', row.displayName) : row.displayName}</span>
                {row.username ? <span className="list-row-subtitle">@{row.username}</span> : null}
              </span>
              <span className="list-row-trailing" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                {row.isChannelAdmin ? <span className="cp-badge">{t('people.admin')}</span> : null}
                {canManage && !row.isSelf ? <ConfirmButton label={t('people.remove')} confirmLabel={t('web.channelPanel.removeConfirm')} onConfirm={() => remove(row)} /> : null}
              </span>
            </div>
          ))}
        </Group>
      ) : null}

      {channel.type === 'G' && roster ? (
        <Group>
          {group.canAddPeople ? (
            <button className="list-row clickable" onClick={() => setAdding(true)}>
              <span className="list-row-text">
                <span className="list-row-title cp-accent">{t('people.groupAdd')}</span>
                <span className="list-row-subtitle">{tf('people.groupSeats', group.freeSeats)}</span>
              </span>
            </button>
          ) : null}
          <div className="cp-note">{group.canAddPeople ? t('people.groupNote') : tf('people.groupFull', ChannelMembersPresentation.maxGroupMembers)}</div>
        </Group>
      ) : null}

      {adding ? (
        <AddPeopleDialog
          channel={channel}
          teamId={channel.teamId || shell.team?.id || ''}
          existingIds={new Set(roster?.members.map((m) => m.userId) ?? [])}
          maxSelectable={channel.type === 'G' ? group.freeSeats : null}
          onClose={() => setAdding(false)}
          onAdded={() => void load()}
        />
      ) : null}
    </>
  );
}
